/**
 * SEO: meta, Open Graph, Twitter, geo (lokalni SEO) i JSON-LD strukturirani podaci
 */

import React from "react"
import PropTypes from "prop-types"
import { Helmet } from "react-helmet"
import { useStaticQuery, graphql } from "gatsby"
import { useLocation } from "@reach/router"

export const BUSINESS = {
  name: "SF Konzalting",
  email: "info@sfkonzalting.hr",
  telephone: ["+385915234932", "+385959007687"],
  street: "Put Petrića 45 E",
  postalCode: "23000",
  city: "Zadar",
  region: "Zadarska županija",
  regionCode: "HR-13",
  country: "HR",
  lat: 44.12542,
  lng: 15.235267,
  sameAs: [
    "https://web.facebook.com/SF-Konzalting-432018117547648/",
    "https://www.instagram.com/sfkonzalting/",
    "https://www.linkedin.com/in/sf-konzalting-business-consulting-services-35618a187/",
  ],
  services: [
    "Izrada EU i nacionalnih projekata",
    "Poslovno savjetovanje i izobrazba",
    "Izrada poslovnih planova",
    "Investicijske studije",
    "Cost-benefit analize",
    "Priprema dokumentacije za HAMAG-BICRO i HBOR",
  ],
  founders: ["Jelena Ferrelli", "Željka Smoljan"],
}

const stripHtml = (s = "") =>
  s
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&#8211;/g, "–")
    .replace(/&[a-z#0-9]+;/gi, " ")
    .replace(/\s+/g, " ")
    .trim()

const truncate = (s, n = 160) =>
  s.length > n ? s.slice(0, n - 1).replace(/\s+\S*$/, "") + "…" : s

const absolute = (siteUrl, url) =>
  !url ? url : /^https?:\/\//.test(url) ? url : siteUrl + (url.startsWith("/") ? url : "/" + url)

export const businessJsonLd = siteUrl => ({
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "@id": `${siteUrl}/#organization`,
  name: BUSINESS.name,
  url: `${siteUrl}/`,
  logo: `${siteUrl}/sflogo.png`,
  image: `${siteUrl}/og-image.png`,
  email: BUSINESS.email,
  telephone: BUSINESS.telephone[0],
  contactPoint: BUSINESS.telephone.map(t => ({
    "@type": "ContactPoint",
    telephone: t,
    contactType: "customer service",
    areaServed: "HR",
    availableLanguage: ["hr"],
  })),
  address: {
    "@type": "PostalAddress",
    streetAddress: BUSINESS.street,
    postalCode: BUSINESS.postalCode,
    addressLocality: BUSINESS.city,
    addressRegion: BUSINESS.region,
    addressCountry: BUSINESS.country,
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: BUSINESS.lat,
    longitude: BUSINESS.lng,
  },
  hasMap: `https://www.openstreetmap.org/?mlat=${BUSINESS.lat}&mlon=${BUSINESS.lng}#map=17/${BUSINESS.lat}/${BUSINESS.lng}`,
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "08:00",
      closes: "16:00",
    },
  ],
  areaServed: [
    { "@type": "City", name: "Zadar" },
    { "@type": "AdministrativeArea", name: "Zadarska županija" },
    { "@type": "Country", name: "Hrvatska" },
  ],
  founder: BUSINESS.founders.map(name => ({ "@type": "Person", name })),
  knowsAbout: ["EU fondovi", "HAMAG-BICRO", "HBOR", "NPOO", "poslovno planiranje"],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Usluge",
    itemListElement: BUSINESS.services.map(name => ({
      "@type": "Offer",
      itemOffered: { "@type": "Service", name },
    })),
  },
  sameAs: BUSINESS.sameAs,
})

const SEO = ({
  description,
  lang,
  meta,
  title,
  image,
  type,
  datePublished,
  dateModified,
  breadcrumbs,
}) => {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            description
            siteUrl
            title
            keywords
          }
        }
      }
    `
  )
  const { pathname } = useLocation()

  const siteUrl = site.siteMetadata.siteUrl.replace(/\/$/, "")
  const defaultTitle = site.siteMetadata.title
  const metaDescription = truncate(
    stripHtml(description) || site.siteMetadata.description.trim()
  )
  const fullTitle = title ? `${title} | ${defaultTitle}` : defaultTitle
  const url = `${siteUrl}${pathname || "/"}`
  const metaImage = absolute(siteUrl, image) || `${siteUrl}/og-image.png`

  const jsonLd = [
    businessJsonLd(siteUrl),
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${siteUrl}/#website`,
      url: `${siteUrl}/`,
      name: BUSINESS.name,
      inLanguage: "hr-HR",
      publisher: { "@id": `${siteUrl}/#organization` },
    },
  ]
  if (breadcrumbs && breadcrumbs.length) {
    jsonLd.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [{ name: "Početna", path: "/" }, ...breadcrumbs].map(
        (b, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: b.name,
          item: `${siteUrl}${b.path}`,
        })
      ),
    })
  }
  if (type === "article") {
    jsonLd.push({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: stripHtml(title),
      description: metaDescription,
      image: [metaImage],
      datePublished,
      dateModified: dateModified || datePublished,
      inLanguage: "hr-HR",
      mainEntityOfPage: url,
      author: { "@id": `${siteUrl}/#organization` },
      publisher: { "@id": `${siteUrl}/#organization` },
    })
  }

  return (
    <Helmet
      htmlAttributes={{ lang }}
      title={title}
      titleTemplate={`%s | ${defaultTitle}`}
      defaultTitle={defaultTitle}
      meta={[
        { name: `description`, content: metaDescription },
        { name: `keywords`, content: site.siteMetadata.keywords },
        { name: `robots`, content: `index, follow, max-image-preview:large` },
        { property: `og:locale`, content: `hr_HR` },
        { property: `og:site_name`, content: BUSINESS.name },
        { property: `og:title`, content: fullTitle },
        { property: `og:description`, content: metaDescription },
        { property: `og:type`, content: type === "article" ? `article` : `website` },
        { property: `og:url`, content: url },
        { property: `og:image`, content: metaImage },
        { name: `twitter:card`, content: `summary_large_image` },
        { name: `twitter:title`, content: fullTitle },
        { name: `twitter:description`, content: metaDescription },
        { name: `twitter:image`, content: metaImage },
        // lokalni SEO
        { name: `geo.region`, content: BUSINESS.regionCode },
        { name: `geo.placename`, content: BUSINESS.city },
        { name: `geo.position`, content: `${BUSINESS.lat};${BUSINESS.lng}` },
        { name: `ICBM`, content: `${BUSINESS.lat}, ${BUSINESS.lng}` },
      ]
        .concat(
          type === "article" && datePublished
            ? [{ property: `article:published_time`, content: datePublished }]
            : []
        )
        .concat(meta)}
    >
      <link
        href="https://api.tiles.mapbox.com/mapbox-gl-js/v1.3.1/mapbox-gl.css"
        rel="stylesheet"
      />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  )
}

SEO.defaultProps = {
  lang: `hr`,
  meta: [],
  description: ``,
  type: `website`,
}

SEO.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string,
  image: PropTypes.string,
  type: PropTypes.string,
  datePublished: PropTypes.string,
  dateModified: PropTypes.string,
  breadcrumbs: PropTypes.arrayOf(PropTypes.object),
}

export default SEO
